/**
 * Pricing Plan — editor component.
 *
 * The plan is a container whose parts are real blocks, so selecting the price
 * gives you the price's typography and colour panels and nothing else — which
 * is the whole point of splitting them out.
 *
 * It used to be locked shut. That guaranteed the layout presets a flat, known
 * sequence to reshuffle, and in exchange forbade every card shape that is not
 * a reshuffle of it — a card with no price, a name and amount side by side, a
 * chooser between the two. The presets still only reach direct children, which
 * is inherent to `order` and `grid-area`; nesting a part simply opts it out.
 */
import { __ } from '@wordpress/i18n';
import {
  useBlockProps,
  useInnerBlocksProps,
  InspectorControls,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import { PanelBody, SelectControl } from '@wordpress/components';
import { useSelect, useDispatch, useRegistry } from '@wordpress/data';
import { useEffect, useRef } from '@wordpress/element';

import { PLAN_TEMPLATE } from '../../_shared/pricing-defaults';
import {
  planLayoutOverrideOptions,
  resolvePlanLayout,
  collectPartAlignmentTargets,
} from '../../_shared/pricing-layout-presets';
import { getBlockGapValue } from '../../../utils/helpers';
import '../editor.scss';

export default function Edit({ attributes, setAttributes, context, clientId }) {
  const { layoutPreset } = attributes;

  const preset = resolvePlanLayout(
    layoutPreset,
    context['brandy/pricingPlanLayout']
  );

  /*
   * Mirrors the badge test in render.php: the class tracks whether a badge is
   * on screen, not whether the plan is featured, because the split-header grid
   * reserves a row for it and an empty declared row still takes its gap.
   */
  const hasBadge = useSelect(
    (select) =>
      select('core/block-editor')
        .getBlocks(clientId)
        .some(
          (b) =>
            b.name === 'brandy/pricing-text' &&
            b.attributes.role === 'badge' &&
            !!b.attributes.content?.trim()
        ),
    [clientId]
  );

  /*
   * Re-align the parts the preset cannot reach.
   *
   * The preset moves the card's own pieces with CSS, but the feature list and
   * the button have their own alignment controls — switching to the centred
   * layout and finding the ticks still hard left is the gap this closes.
   *
   * ONLY ON A CHANGE, NEVER ON MOUNT. Seeding on the first run would fire on
   * every page load and quietly overwrite whatever the author had aligned by
   * hand — the ref is what makes "you can still change it afterwards" true.
   * Changing the preset again seeds again, which is the point.
   */
  const registry = useRegistry();
  const { updateBlockAttributes } = useDispatch(blockEditorStore);
  const previousPreset = useRef();

  useEffect(() => {
    if (previousPreset.current === undefined) {
      previousPreset.current = preset;
      return;
    }
    if (previousPreset.current === preset) {
      return;
    }
    previousPreset.current = preset;

    const { lists, buttons, alignment } = collectPartAlignmentTargets(
      preset,
      registry.select(blockEditorStore).getBlocks(clientId)
    );

    if (!lists.length && !buttons.length) {
      return;
    }

    // Batched so the store notifies subscribers once for the whole
    // re-alignment rather than once per block — six buttons and three lists
    // is nine renders of the canvas otherwise.
    registry.batch(() => {
      if (lists.length) {
        updateBlockAttributes(lists, { alignment: alignment.list });
      }
      if (buttons.length) {
        updateBlockAttributes(buttons, {
          layout: { type: 'flex', justifyContent: alignment.button },
        });
      }
    });
  }, [preset, clientId, registry, updateBlockAttributes]);

  /*
   * Core's Block spacing value, applied here because core itself only writes
   * blockGap through the layout support — which this block does not declare.
   * The same helper render.php uses: without it the card's spacing moved on
   * the front end while the editor kept showing the theme's gap.
   */
  const gap = getBlockGapValue(attributes.style?.spacing?.blockGap);

  const blockProps = useBlockProps({
    className: [
      'brandy-pricing-plan',
      `is-plan-${preset}`,
      hasBadge && 'has-badge',
    ]
      .filter(Boolean)
      .join(' '),
    // Left unset when empty so the stylesheet's fallback chain decides, the
    // same way render.php omits the property.
    style: gap ? { '--pricing-plan-gap': gap } : undefined,
  });

  const innerBlocksProps = useInnerBlocksProps(blockProps, {
    /*
     * Open. The parts are what a plan is made of, not what it is limited to:
     * a card that has no price, or that puts its name and amount in a group
     * side by side, is a card a real pricing page asks for. The template is
     * the starting point, and anything unwanted can be deleted.
     *
     * No `allowedBlocks` either — the moment you are building a card by hand
     * you want an image, a heading, a spacer. The parts stay where they belong
     * through their own `ancestor`, not through a list here.
     */
    template: PLAN_TEMPLATE,
    templateLock: false,
  });

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Plan', 'brandy-blocks')}>
          <SelectControl
            label={__('Layout', 'brandy-blocks')}
            value={layoutPreset || ''}
            options={planLayoutOverrideOptions()}
            onChange={(value) => setAttributes({ layoutPreset: value })}
            help={__(
              'Overrides the layout the table sets, for this plan only. Presets move the card’s own parts — a part you have nested inside a group is positioned by that group instead.',
              'brandy-blocks'
            )}
          />
        </PanelBody>
      </InspectorControls>

      <div {...innerBlocksProps} />
    </>
  );
}
